import type {DragEvent, ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {EmailMakerSocialLinkSuggestionsDropdown} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {isValidForLink} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {getSanitizedSocialUrl} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import type {SocialLinkRowData} from '../../social-links-control.types';
import {stripHttpsPrefix} from '../../social-links-control.types';
import styles from './social-link-item.module.scss';

export interface SocialLinkItemProps {
  row: SocialLinkRowData;
  iconStyle: string;
  onRemove: (id: string) => void;
  onUrlCommit: (id: string, url: string) => void;
  onDragStart: (e: DragEvent<HTMLDivElement>, id: string) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>, id: string) => void;
}

export function SocialLinkItem({row, iconStyle, onRemove, onUrlCommit, onDragStart, onDragOver, onDrop}: SocialLinkItemProps): ReactElement {
  const [hasError, setHasError] = useState<boolean>(false);

  const handleInputFocus = useCallback((_dataId: string, _value: string): void => {
    setHasError(false);
  }, []);

  const handleInputBlur = useCallback(
    (dataId: string, value: string): void => {
      const normalized = getSanitizedSocialUrl(value.trim());

      if (normalized === '' || isValidForLink(normalized)) {
        setHasError(false);
      } else {
        setHasError(true);
        return;
      }

      onUrlCommit(dataId, normalized);
    },
    [onUrlCommit]
  );

  const handleRemove = useCallback((): void => {
    onRemove(row.id);
  }, [onRemove, row.id]);

  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      onDragStart(e, row.id);
    },
    [onDragStart, row.id]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      onDragOver(e);
    },
    [onDragOver]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      onDrop(e, row.id);
    },
    [onDrop, row.id]
  );

  const platformURL = stripHttpsPrefix(row.platformURL);

  function renderError(): ReactElement | null {
    if (!hasError) {
      return null;
    }
    return (
      <div className={`error-message-container ${styles.errorContainer}`}>
        <Icon aria-hidden className="_unmargin content-danger" icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_invalid_url')} size={TextSize.SM} className="invalid-url-message content-danger" />
      </div>
    );
  }

  return (
    <div className={`social-draggable-container ${styles.container}`} draggable onDragOver={handleDragOver} onDragStart={handleDragStart} onDrop={handleDrop}>
      <div className={`social-link-input-container ${styles.inputContainer}`}>
        <div className={styles.row}>
          <Icon aria-hidden className={`js-slide-dragger social-link-drag-handler ${styles.dragHandle}`} icon="icon-draggable" size={IconSize.SIZE_ICON_16} />
          <div className={`social-link-input-img-container ${styles.imgContainer}`}>
            <img alt={row.platformName} className={`social-link-input-img ${iconStyle} ${styles.platformImg}`} src={row.logoURL} />
          </div>
          <div className={styles.inputWrapper}>
            <EmailMakerSocialLinkSuggestionsDropdown
              dropdownId={`social-link-dropdown-${row.id}`}
              inputId={`social-link-input-${row.id}`}
              platform={row.id}
              socialLinkInputCustomHandlers={{
                onInputFocus: handleInputFocus,
                onInputBlur: handleInputBlur,
              }}
              inputFieldProps={{
                inputClassName: `social-link-input-url js-social-link-input-url border-s-darker radius-4 body-sm-regular content-body`,
                placeholder: platformURL !== '' ? platformURL : window.i18next.t('pmwjs_add_profile_url'),
                input: row.baseURL,
                size: InputFieldSize.XSMALL,
              }}
              inputSuggestionsDropdownProps={{
                popupClassName: 'social-link-suggestions-dropdown',
                popupAlignment: ALIGNMENT_TYPE.BOTTOM_END,
                popupPosition: DROPDOWN_POSITION.FIXED,
                truncateText: true,
                showSuggestionAsTitleOnHover: true,
              }}
            />
          </div>
          <Icon
            ariaLabel={window.i18next.t('pmwjs_delete')}
            className={`delete-icon js-delete-social-link ${styles.deleteBtn}`}
            icon="icon-delete"
            isUsedAsButton
            onClick={handleRemove}
            shape={IconShape.SQUARE}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST}
          />
        </div>
        {renderError()}
      </div>
    </div>
  );
}
